import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
  Typography,
  Divider,
  TableContainer,
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell as MuiTableCell,
  TableBody,
  Stack,
  Button,
  styled,
  Tabs,
  Tab,
  MenuItem
} from "@mui/material";
import { useMembersQuery } from "src/hooks/query/useMembersQuery";
import { useSelector } from "src/store/Store";
import { selectId, selectUser, selectUserCompany } from "src/store/authSlice";
import { Select, TextField as CommonTextField } from "./forms";
import Modal from "./Modal";
import { SelectableTable } from ".";
import { TRow } from "./SelectableTable";
import { useForm, useController } from "react-hook-form";
import NiceModal from "@ebay/nice-modal-react";

const TextField = styled(CommonTextField)`
  .MuiInputBase-root {
    background-color: #fff;
  }
`;

const TableCell = styled(MuiTableCell)`
  background-color: #f6f6f6;
  text-align: center;
`;

interface FormInputs {
  mbr_id: string;
  mbr_nm: string;
}

function AdditionalPartnerTable({
  title,
  headers,
  rows,
  onHandlePartners,
  onDeletePartner,
  editMode,
  addEnable = true
}: {
  title: string;
  headers: { key: string; label: string }[];
  rows: { [key in string]: string }[];
  onHandlePartners: (item: any) => void;
  onDeletePartner?: (item: any) => void;
  editMode: boolean;
  addEnable?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<TRow>();
  const [memberAuth, setMemberAuth] = useState("all");

  const [filterOptions, setFilterOptions] = useState({
    mbr_id: "",
    mbr_nm: "",
    mbr_auth: ""
  });
  const [paginationOptions, setPaginationOptions] = useState({
    currentPage: "1",
    recordSize: "10"
  });

  const memberId = useSelector(selectId);
  const { name: memberName } = useSelector(selectUser);
  const { branchName, departmentName } = useSelector(selectUserCompany);

  const { data: memberList } = useMembersQuery(
    filterOptions,
    paginationOptions,
    "1"
  );

  const methods = useForm<FormInputs>({
    defaultValues: {
      mbr_id: "",
      mbr_nm: ""
    }
  });

  const { field: memberIdControl } = useController({
    name: "mbr_id",
    control: methods.control
  });

  const { field: memberNameControl } = useController({
    name: "mbr_nm",
    control: methods.control
  });

  const onSubmit = useCallback(() => {
    const mbr_id = memberIdControl.value;
    const mbr_nm = memberNameControl.value;

    setFilterOptions(curr => ({
      ...curr,
      mbr_id,
      mbr_nm
    }));
  }, [memberIdControl.value, memberNameControl.value]);

  useEffect(() => {
    setFilterOptions(curr => ({
      ...curr,
      mbr_auth: memberAuth === "all" ? "" : memberAuth
    }));
  }, [memberAuth]);

  const handleChange = useCallback(
    (event: React.SyntheticEvent, newValue: string) => {
      setMemberAuth(newValue);
    },
    []
  );

  const handleRecordSize = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setPaginationOptions(curr => ({
      ...curr,
      recordSize: e.target.value
    }));
  }, []);

  const handlePrevPage = useCallback(() => {
    if (memberList) {
      const startPage = Number(memberList.pagination.startPage);
      setPaginationOptions(curr => {
        const currPage = Number(curr.currentPage);
        return {
          ...curr,
          currentPage:
            currPage > startPage ? (currPage - 1).toString() : curr.currentPage
        };
      });
    }
  }, [memberList]);

  const handleNextPage = useCallback(() => {
    if (memberList) {
      const endPage = Number(memberList.pagination.endPage);
      setPaginationOptions(curr => {
        const currPage = Number(curr.currentPage);
        return {
          ...curr,
          currentPage:
            currPage < endPage ? (currPage + 1).toString() : curr.currentPage
        };
      });
    }
  }, [memberList]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const handleClose = useCallback(() => {
    setOpen(false);
    memberIdControl.onChange("");
    memberNameControl.onChange("");
  }, [memberIdControl, memberNameControl]);

  const handleAddPartner = useCallback(() => {
    if (selectedRow) {
      onHandlePartners(selectedRow);
    }
    handleClose();
  }, [selectedRow, handleClose, onHandlePartners]);

  return (
    <>
      <div style={{ width: "100%" }}>
        <Stack
          justifyContent="space-between"
          gap="8px"
          direction="row"
          alignItems="center"
          mb="4px"
        >
          <Typography variant="h6">{title}</Typography>
          {editMode && (
            <div>
              <Button
                size="small"
                variant="contained"
                onClick={() =>
                  onHandlePartners({
                    mbr_id: memberId,
                    dprt_nm: branchName,
                    mbr_nm: memberName,
                    brnc_nm: departmentName
                  })
                }
                disabled={!addEnable}
                sx={{ marginRight: "4px" }}
              >
                내정보 추가
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleOpen}
                disabled={!addEnable}
              >
                추가
              </Button>
            </div>
          )}
        </Stack>

        <Divider />
        <TableContainer>
          <MuiTable
            aria-label="custom pagination table"
            sx={{
              whiteSpace: "nowrap"
            }}
          >
            <TableHead>
              <TableRow>
                {headers.map((header, idx) => (
                  <MuiTableCell key={idx} id={header.key} align="center">
                    <Typography variant="h6">{header.label}</Typography>
                  </MuiTableCell>
                ))}
                {editMode && <MuiTableCell />}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map((row, row_idx) => (
                <TableRow key={row_idx}>
                  {headers.map((item, item_idx) => (
                    <TableCell key={item_idx}>{row[item.key]}</TableCell>
                  ))}
                  {editMode && onDeletePartner && (
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          NiceModal.show("confirm", {
                            title: "정말로 삭제하시겠습니까?",
                            message: "이 동작은 되돌릴 수 없습니다."
                          }).then(() => onDeletePartner(row));
                        }}
                      >
                        삭제
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>
        <Divider />
        <Modal
          isView={open}
          title={"담당자 선택"}
          doAction={handleAddPartner}
          cancelAction={handleClose}
          doActionText={"확인"}
        >
          <div>
            <Stack direction="row" gap="15px">
              <Stack
                direction="column"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography variant="h6" gutterBottom>
                  직원ID
                </Typography>
                <TextField
                  size="small"
                  value={memberIdControl.value}
                  onChange={memberIdControl.onChange}
                  variant="outlined"
                  fullWidth
                  placeholder="텍스트를 입력하세요."
                />
              </Stack>
              <Stack
                direction="column"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <Typography variant="h6" gutterBottom>
                  직원명
                </Typography>
                <TextField
                  size="small"
                  value={memberNameControl.value}
                  onChange={memberNameControl.onChange}
                  variant="outlined"
                  fullWidth
                  placeholder="텍스트를 입력하세요."
                />
              </Stack>
              <Button size="small" onClick={onSubmit}>
                검색
              </Button>
            </Stack>
          </div>

          <div>
            <Tabs
              value={memberAuth}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="전체" id="all" value="all" />
              <Tab label="지사관리자" id="MA0002" value="MA0002" />
              <Tab label="부서관리자" id="MA0003" value="MA0003" />
              <Tab label="팀장" id="MA0004" value="MA0004" />
            </Tabs>
            <div role="tabpanel">
              <div style={{ textAlign: "right", marginBottom: "4px" }}>
                <Button
                  size="small"
                  color="inherit"
                  disableRipple
                  onClick={handlePrevPage}
                >
                  {"<"}
                </Button>
                {`${memberList?.paging.currentPage || "-"} / ${
                  memberList?.pagination.endPage || "-"
                }`}
                <Button
                  size="small"
                  color="inherit"
                  disableRipple
                  onClick={handleNextPage}
                >
                  {">"}
                </Button>

                <Select
                  value={paginationOptions.recordSize}
                  onChange={handleRecordSize}
                  size="small"
                >
                  {["10", "15", "20"].map((item, idx) => (
                    <MenuItem key={idx} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <SelectableTable
                onRowSelect={item => setSelectedRow(item)}
                checkId="mbr_id"
                headers={[
                  { label: "부서", key: "dprt_nm" },
                  { label: "직원ID", key: "mbr_id" },
                  { label: "직원명", key: "mbr_nm" },
                  { label: "직원연락처", key: "mbr_tel" }
                ]}
                rows={memberList?.data || []}
              />
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
}

export default AdditionalPartnerTable;
